import { useCallback, useEffect, useState, useSyncExternalStore } from 'react'
import { slotWindow } from '../lib/slot-time'
import { api } from '../lib/api'
import type { Slot } from '../lib/types'
import { useSession } from '../lib/session'
import { nowMs } from '../lib/clock'

/**
 * A single shared slot cache.
 *
 * The programme is needed by the schedule, the scanner, the certificate screen and
 * the tab badge at once. Fetching per-screen would mean four `/getSlot` round trips
 * on every navigation, so it lives in one module-level store that components read
 * through `useSyncExternalStore`.
 */

interface SlotsState {
  slots: Slot[]
  onGoing: Slot[]
  totalCheckin: number
  loading: boolean
  error: string | null
  loadedAt: number
}

const EMPTY: SlotsState = {
  slots: [],
  onGoing: [],
  totalCheckin: 0,
  loading: false,
  error: null,
  loadedAt: 0,
}

let state: SlotsState = EMPTY
const listeners = new Set<() => void>()
let inFlight: Promise<void> | null = null

function emit(next: Partial<SlotsState>) {
  state = { ...state, ...next }
  listeners.forEach((fn) => fn())
}

function subscribe(fn: () => void) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

function snapshot() {
  return state
}

/** Data older than this is refetched when a screen mounts. */
const STALE_MS = 45_000

export async function loadSlots(
  email: string,
  token: string,
  { force = false }: { force?: boolean } = {},
): Promise<void> {
  if (inFlight) return inFlight
  if (!force && Date.now() - state.loadedAt < STALE_MS) return

  emit({ loading: true, error: null })

  inFlight = api
    .getSlots(email, token)
    .then((res) => {
      emit({
        /*
         * /getSlot has no ORDER BY, so rows arrive in whatever order MSSQL feels like -
         * on event 6 that put day 3 ahead of day 2, and Home's "Upcoming" (which takes
         * the first two future slots) announced Friday's closing ceremony as next.
         * Programme sorted inside groupByDay and never noticed. Sort once here so every
         * reader sees chronological order.
         */
        slots: [...(res.slots ?? [])].sort((a, b) => slotWindow(a).start - slotWindow(b).start),
        onGoing: res.onGoing ?? [],
        totalCheckin: Number(res.total_checkin) || 0,
        loading: false,
        error: null,
        loadedAt: Date.now(),
      })
    })
    .catch((err: unknown) => {
      emit({
        loading: false,
        error: err instanceof Error ? err.message : 'Could not load the programme.',
      })
    })
    .finally(() => {
      inFlight = null
    })

  return inFlight
}

/** Applied locally after a successful scan so the row updates without a refetch. */
export function markCheckedIn(slotId: number) {
  emit({
    slots: state.slots.map((s) =>
      s.id === slotId ? { ...s, checkin_status: true, show_btn: false } : s,
    ),
    totalCheckin: state.slots.some((s) => s.id === slotId && !s.checkin_status)
      ? state.totalCheckin + 1
      : state.totalCheckin,
  })
}

export function resetSlots() {
  state = EMPTY
  listeners.forEach((fn) => fn())
}

export function useSlots() {
  const { session } = useSession()
  const snap = useSyncExternalStore(subscribe, snapshot, snapshot)

  const refresh = useCallback(() => {
    if (session) void loadSlots(session.email, session.at, { force: true })
  }, [session])

  useEffect(() => {
    if (!session) return
    void loadSlots(session.email, session.at)

    // Re-check when the phone comes back from sleep or the tab is refocused —
    // a check-in window can open while the screen is off.
    function onVisible() {
      if (document.visibilityState === 'visible' && session) {
        void loadSlots(session.email, session.at, { force: true })
      }
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [session])

  return { ...snap, refresh }
}

export function useCheckinCount(): number {
  return useSyncExternalStore(subscribe, snapshot, snapshot).totalCheckin
}

/**
 * Re-renders on an interval so relative times ("12 min left") and the check-in
 * window stay honest without the user pulling to refresh.
 */
export function useTick(intervalMs = 30_000): number {
  const [now, setNow] = useState(() => nowMs())

  useEffect(() => {
    const id = setInterval(() => setNow(nowMs()), intervalMs)
    return () => clearInterval(id)
  }, [intervalMs])

  return now
}
